import dayjs from 'dayjs';
import { PostAttachmentSchema, PostPlatformSchema, PostSchema } from '@ametista-ai/api-schema';
import { getIncompleteBlockers, isAwaitingPostApproval } from '@ametista-ai/api-schema/agencyTask';
import { PostStatus } from '@ametista-ai/api-schema/http/types';

// A subset of AgencyTaskSchema covering only the fields these helpers actually read.
type TaskLike = {
  id: string;
  title: string;
  completedAt?: Date | null;
  dueDate?: Date | null;
  blockedBy?: string[] | null;
  blockedByPostApproval?: boolean | null;
};

/**
 * Post lifecycle stage — a computed waterfall, not a stored field.
 * Checked in order from most-final to least-final; first match wins.
 */
export function getPostStatus(post: PostSchema, postTasks: TaskLike[]) {
  if (post.publishedAt) {
    return PostStatus.Published;
  }

  if (post.archived || !post.date) {
    return PostStatus.Archived;
  }

  if (post.scheduledAt) {
    return PostStatus.Scheduled;
  }

  if (post.approvedAt) {
    return PostStatus.Approved;
  }

  if (postTasks.length === 0) {
    return PostStatus.Draft;
  }

  if (post.isFinished) {
    return PostStatus.PendingApproval;
  }

  return PostStatus.InProgress;
}

/** Attention flag, kept separate from the lifecycle stage: an open task past its due date. */
export function getPostNeedsAttention(post: PostSchema, postTasks: TaskLike[]): boolean {
  return postTasks.some(
    (task) => !task.completedAt && task.dueDate && dayjs(task.dueDate).isBefore(dayjs())
  );
}

/** Attachments selected on a single platform, resolved from the post's full attachment list. */
export function getPostPlatformAttachments(
  postPlatform: PostPlatformSchema | undefined,
  postAttachments: PostAttachmentSchema[]
): PostAttachmentSchema[] {
  return (postPlatform?.attachmentIds ?? [])
    .map((id) => postAttachments.find((attachment) => attachment.id === id))
    .filter((attachment) => attachment !== undefined);
}

export function getPostCover(
  postPlatforms: PostPlatformSchema[],
  postAttachments: PostAttachmentSchema[]
) {
  const attachmentIds = postPlatforms.flatMap((platform) => platform.attachmentIds ?? []);

  const sortedAttachments = attachmentIds
    .map((id) => postAttachments.find((attachment) => attachment.id === id))
    .filter((attachment) => attachment !== undefined);

  return sortedAttachments?.[0] ?? null;
}

/**
 * Why a task is blocked: the open sibling tasks it waits on, and/or the post
 * approval it is gated on. `isBlocked` is true if either applies.
 */
export function getTaskBlockState<T extends TaskLike>(
  task: T,
  tasks: T[],
  post: Pick<PostSchema, 'approvedAt'>
) {
  const blockedByTasks = getIncompleteBlockers(task, tasks);
  const awaitingPostApproval = isAwaitingPostApproval(task, !!post.approvedAt);
  return {
    blockedByTasks,
    awaitingPostApproval,
    isBlocked: blockedByTasks.length > 0 || awaitingPostApproval,
  };
}
